import React, { useState } from "react";
import { TrashIcon, PencilIcon } from "@heroicons/react/24/outline";
import { Roles } from "../../constants/roles";
import DeleteConfirmationModal from "../modals/DeleteConfirmationModal";

const CommentsSection = ({
  comments,
  onDelete,
  onEdit,
  user,
  role,
  isMobile,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);

  if (!comments || Object.keys(comments).length === 0) return null;

  const canDeleteComment = (c) =>
    user?.id === c.authorId || [Roles.ADMIN, Roles.CREATOR].includes(role);

  const canEditComment = (c) => user?.id === c.authorId;

  const handleDeleteClick = (ts) => {
    setCommentToDelete(ts);
    setShowDeleteModal(true);
  };

  const confirmDeleteComment = () => {
    if (commentToDelete) {
      onDelete(commentToDelete);
      setShowDeleteModal(false);
      setCommentToDelete(null);
    }
  };

  const sortedComments = Object.entries(comments).sort(
    ([a], [b]) => parseInt(b) - parseInt(a)
  );

  if (isMobile) {
    // — MOBİL: senin verdiğin mobil JSX —
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow p-5 md:p-6">
        <h2 className="text-[20px] md:text-[22px] font-[800] text-[#1E293B]">
          Comments
        </h2>
        <div className="border-b border-gray-200 mt-3 mb-2" />
        <div className="divide-y divide-gray-200">
          {sortedComments.map(([timestamp, comment]) => {
            const displayTime = new Date(
              parseInt(comment.editTimestamp || timestamp)
            ).toLocaleString();
            return (
              <div key={timestamp} className="relative py-4">
                {(canDeleteComment(comment) || canEditComment(comment)) && (
                  <div className="absolute top-3 right-0 flex items-center gap-2">
                    {canEditComment(comment) && (
                      <button
                        onClick={() => onEdit(timestamp, comment)}
                        className="text-slate-600 hover:text-slate-800"
                        title="Edit comment"
                      >
                        <PencilIcon className="w-5 h-5" />
                      </button>
                    )}
                    {canDeleteComment(comment) && (
                      <button
                        onClick={() => handleDeleteClick(timestamp)}
                        className="text-slate-600 hover:text-red-600"
                        title="Delete comment"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                )}
                <p className="text-[14px] leading-6">
                  <span className="font-[700] text-slate-800">
                    {comment.author}
                  </span>
                  <span className="text-slate-400"> – {displayTime}</span>
                  {comment.editTimestamp && (
                    <span className="ml-1 text-xs text-slate-400">
                      (edited)
                    </span>
                  )}
                </p>
                <p className="mt-2 text-[15px] text-slate-700 break-words">
                  {comment.text}
                </p>
              </div>
            );
          })}
        </div>

        {showDeleteModal && (
          <DeleteConfirmationModal
            isOpen={showDeleteModal}
            closeModal={() => {
              setShowDeleteModal(false);
              setCommentToDelete(null);
            }}
            handleDelete={confirmDeleteComment}
            entityName="this comment"
          />
        )}
      </div>
    );
  }

  // — WEB: senin verdiğin web JSX —
  return (
    <div className="mb-4 mt-4">
      <h2 className="text-[24px] font-[800]">Comments</h2>
      <div className="space-y-4">
        {sortedComments.map(([timestamp, comment]) => (
          <div
            key={timestamp}
            className="relative bg-white rounded-md shadow p-6"
          >
            {(canDeleteComment(comment) || canEditComment(comment)) && (
              <div className="absolute top-2 right-2 flex space-x-2">
                {canEditComment(comment) && (
                  <button
                    onClick={() => onEdit(timestamp, comment)}
                    className="text-blue-500 hover:text-blue-700"
                    title="Edit Comment"
                  >
                    <PencilIcon className="w-5 h-5" />
                  </button>
                )}
                {canDeleteComment(comment) && (
                  <button
                    onClick={() => handleDeleteClick(timestamp)}
                    className="text-red-500 hover:text-red-700"
                    title="Delete Comment"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                )}
              </div>
            )}
            <p className="text-sm text-gray-600 mb-1 break-words">
              <strong>{comment.author}</strong> -{" "}
              {new Date(
                parseInt(comment.editTimestamp || timestamp)
              ).toLocaleString()}
              {comment.editTimestamp && (
                <span className="ml-1 text-xs text-gray-500">(edited)</span>
              )}
            </p>
            <p className="text-gray-800 break-words">{comment.text}</p>
          </div>
        ))}
      </div>

      {showDeleteModal && (
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          closeModal={() => {
            setShowDeleteModal(false);
            setCommentToDelete(null);
          }}
          handleDelete={confirmDeleteComment}
          entityName="this comment"
        />
      )}
    </div>
  );
};

export default CommentsSection;
